import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { setCsrfToken, clearCsrfToken, sanitizeInput } from '../utils/security';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token refresh interval (refresh 2 minutes before expiry)
const TOKEN_REFRESH_BUFFER = 2 * 60 * 1000; // 2 minutes in ms

// Inactivity timeout (5 minutes)
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in ms

export const AuthProvider = ({ children }) => {
    // SECURITY: Store access token in memory only (not localStorage)
    // This protects against XSS attacks stealing tokens
    const [user, setUser] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);
    const [requires2FA, setRequires2FA] = useState(false);
    const refreshTimerRef = useRef(null);
    const tokenExpiryRef = useRef(null);
    const isRefreshingRef = useRef(false);
    const inactivityTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Reset inactivity timer on user activity
    const resetInactivityTimer = useCallback(() => {
        lastActivityRef.current = Date.now();
        
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        
        // Only set timer if user is logged in
        if (user) {
            inactivityTimerRef.current = setTimeout(() => {
                console.log('Session expired due to inactivity');
                // Logout user
                setUser(null);
                setAccessToken(null);
                clearCsrfToken();
                // Clear refresh token cookie by calling logout endpoint
                axios.post(`${API_URL}/auth/logout`).catch(() => {});
            }, INACTIVITY_TIMEOUT);
        }
    }, [user]);

    // Setup activity listeners
    useEffect(() => {
        if (!user) return;

        const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
        
        const handleActivity = () => {
            resetInactivityTimer();
        };

        // Add event listeners
        activityEvents.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });

        // Initial timer setup
        resetInactivityTimer();

        // Cleanup
        return () => {
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [user, resetInactivityTimer]);

    // Configure axios defaults
    useEffect(() => {
        // Always include credentials (cookies) in requests
        axios.defaults.withCredentials = true;
    }, []);

    // Setup axios interceptor for automatic token refresh
    useEffect(() => {
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                // Add access token to all requests if available
                if (accessToken) {
                    config.headers['Authorization'] = `Bearer ${accessToken}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                
                // If 401 and not already retried, try to refresh token
                if (error.response?.status === 401 && !originalRequest._retry && !isRefreshingRef.current) {
                    originalRequest._retry = true;
                    
                    try {
                        // Call refresh directly to avoid stale closure
                        isRefreshingRef.current = true;
                        const response = await axios.post(`${API_URL}/auth/refresh`, {});
                        const { access_token, token: newToken, csrf_token } = response.data;
                        const token = access_token || newToken;
                        
                        if (token) {
                            setAccessToken(token);
                            if (csrf_token) {
                                setCsrfToken(csrf_token);
                            }
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            return axios(originalRequest);
                        }
                    } catch (refreshError) {
                        // Refresh failed, clear state
                        setAccessToken(null);
                        setUser(null);
                        clearCsrfToken();
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshingRef.current = false;
                    }
                }
                
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [accessToken]);

    // Setup token refresh timer
    const setupRefreshTimer = useCallback((expiresIn) => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        // Calculate when to refresh (2 minutes before expiry)
        const refreshTime = (expiresIn * 1000) - TOKEN_REFRESH_BUFFER;
        
        if (refreshTime > 0) {
            refreshTimerRef.current = setTimeout(async () => {
                try {
                    // Inline refresh logic to avoid stale closure
                    if (isRefreshingRef.current) return;
                    isRefreshingRef.current = true;
                    
                    const response = await axios.post(`${API_URL}/auth/refresh`, {});
                    const { access_token, token: newToken, expires_in, csrf_token } = response.data;
                    const token = access_token || newToken;
                    
                    if (token) {
                        setAccessToken(token);
                        if (csrf_token) {
                            setCsrfToken(csrf_token);
                        }
                    }
                    isRefreshingRef.current = false;
                } catch (error) {
                    console.error('Auto refresh failed:', error);
                    isRefreshingRef.current = false;
                }
            }, refreshTime);
        }

        // Store expiry time
        tokenExpiryRef.current = Date.now() + (expiresIn * 1000);
    }, []);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    }, []);

    // On initial load, try to restore session via refresh token
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // Try to refresh the session using HTTP-only cookie
                if (isRefreshingRef.current) return;
                isRefreshingRef.current = true;
                
                const response = await axios.post(`${API_URL}/auth/refresh`, {});
                const { access_token, token: newToken, expires_in, csrf_token } = response.data;
                const token = access_token || newToken;
                
                if (token) {
                    setAccessToken(token);
                    if (csrf_token) {
                        setCsrfToken(csrf_token);
                    }
                    if (expires_in) {
                        // Store expiry time
                        tokenExpiryRef.current = Date.now() + (expires_in * 1000);
                    }
                    // Fetch user data
                    const userResponse = await axios.get(`${API_URL}/auth/me`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    setUser(userResponse.data);
                    setRequiresPasswordReset(userResponse.data.requires_password_reset || false);
                    if (userResponse.data.csrf_token) {
                        setCsrfToken(userResponse.data.csrf_token);
                    }
                }
                isRefreshingRef.current = false;
            } catch (error) {
                // No valid session, user needs to login
                console.log('No active session');
                isRefreshingRef.current = false;
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API_URL}/auth/me`);
            setUser(response.data);
            setRequiresPasswordReset(response.data.requires_password_reset || false);
            
            // Store CSRF token if provided
            if (response.data.csrf_token) {
                setCsrfToken(response.data.csrf_token);
            }
        } catch (error) {
            console.error('Failed to fetch user:', error);
            throw error;
        }
    };

    const refreshToken = async () => {
        if (isRefreshingRef.current) {
            return null;
        }
        
        isRefreshingRef.current = true;
        
        try {
            // Refresh uses HTTP-only cookie automatically (withCredentials: true)
            const response = await axios.post(`${API_URL}/auth/refresh`, {});
            
            const { access_token, token: newToken, expires_in, csrf_token } = response.data;
            const token = access_token || newToken;
            
            if (token) {
                // Store in memory only - NOT in localStorage
                setAccessToken(token);
                
                // Store CSRF token if provided
                if (csrf_token) {
                    setCsrfToken(csrf_token);
                }
                
                // Setup next refresh
                if (expires_in) {
                    setupRefreshTimer(expires_in);
                }
                
                return token;
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            setAccessToken(null);
            setUser(null);
            throw error;
        } finally {
            isRefreshingRef.current = false;
        }
        return null;
    };

    const login = async (username, password, twoFaCode = null) => {
        // Sanitize inputs
        const sanitizedUsername = sanitizeInput(username, 100);
        
        const response = await axios.post(`${API_URL}/auth/login`, { 
            username: sanitizedUsername, 
            password,
            two_fa_code: twoFaCode
        });
        
        // Check if 2FA is required
        if (response.data.requires_2fa) {
            setRequires2FA(true);
            return { requires2FA: true };
        }
        
        const { access_token, token: newToken, user: userData, expires_in, csrf_token } = response.data;
        const token = access_token || newToken;
        
        // Store in memory only - NOT in localStorage
        setAccessToken(token);
        setUser(userData);
        setRequiresPasswordReset(userData.requires_password_reset || false);
        setRequires2FA(false);
        
        // Store CSRF token
        if (csrf_token) {
            setCsrfToken(csrf_token);
        }
        
        // Setup token refresh timer
        if (expires_in) {
            setupRefreshTimer(expires_in);
        }
        
        return userData;
    };

    const completePasswordReset = async (newToken) => {
        // Store new token in memory
        setAccessToken(newToken);
        setRequiresPasswordReset(false);
        await fetchUser();
    };

    const logout = async () => {
        // Clear refresh timer
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }
        
        // Call logout endpoint to revoke refresh token
        try {
            await axios.post(`${API_URL}/auth/logout`, {});
        } catch (error) {
            console.error('Logout API error:', error);
        }
        
        // Clear all auth state
        setAccessToken(null);
        setUser(null);
        setRequiresPasswordReset(false);
        setRequires2FA(false);
        clearCsrfToken();
    };

    const logoutAllDevices = async () => {
        // Clear refresh timer
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }
        
        try {
            await axios.post(`${API_URL}/auth/logout-all`, {});
        } catch (error) {
            console.error('Logout all error:', error);
        }
        
        // Clear all auth state
        setAccessToken(null);
        setUser(null);
        setRequiresPasswordReset(false);
        setRequires2FA(false);
        clearCsrfToken();
    };

    const updateUser = (updatedData) => {
        setUser(prev => ({ ...prev, ...updatedData }));
    };

    // Role check helpers - these are for UI display only
    // Actual authorization is enforced server-side
    const hasUnitAccess = () => {
        const unitRoles = ['network_administrator', 'network_manager', 'local_controller', 'deputy_local_controller', 'unit_duty_officer'];
        return user && unitRoles.includes(user.role);
    };

    const hasGroupAccess = () => {
        const groupRoles = ['group_leader', 'deputy_group_leader', 'group_administrator'];
        return user && (hasUnitAccess() || groupRoles.includes(user.role));
    };

    const canCreateUsers = () => {
        const createRoles = ['network_administrator', 'network_manager', 'local_controller', 'deputy_local_controller', 'unit_duty_officer', 'group_leader', 'deputy_group_leader', 'group_administrator'];
        return user && createRoles.includes(user.role);
    };

    const isNetworkManager = () => {
        return user && (user.role === 'network_manager' || user.role === 'network_administrator');
    };
    
    const isNetworkAdmin = () => {
        return user && user.role === 'network_administrator';
    };

    return (
        <AuthContext.Provider value={{
            user,
            token: accessToken, // Expose for components that need it
            loading,
            login,
            logout,
            logoutAllDevices,
            updateUser,
            hasUnitAccess,
            hasGroupAccess,
            canCreateUsers,
            isNetworkManager,
            isNetworkAdmin,
            isAuthenticated: !!user,
            requiresPasswordReset,
            requires2FA,
            setRequires2FA,
            completePasswordReset,
            refreshToken
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
